import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { apiClient, formatSAR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Building2, Plus, ListChecks, Star, Loader2, BadgeCheck } from "lucide-react";

const STATUS_LABEL = {
  new: { ar: "جديد", en: "New", color: "blue" },
  in_review: { ar: "قيد المراجعة", en: "In review", color: "amber" },
  accepted: { ar: "تم القبول", en: "Accepted", color: "green" },
  in_progress: { ar: "جاري التنفيذ", en: "In progress", color: "purple" },
  completed: { ar: "مكتمل", en: "Completed", color: "emerald" },
  cancelled: { ar: "ملغي", en: "Cancelled", color: "red" },
};

export default function PartnerDashboardPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [partners, setPartners] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([
      apiClient.get("/partners/me"),
      apiClient.get("/service-orders/mine", { params: { role: "partner" } }),
    ]).then(([p, o]) => {
      setPartners(p.data.items || []);
      setOrders(o.data.items || []);
      setLoading(false);
    });
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (user === undefined) return <div className="p-10 text-[#525252]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/partner/dashboard" }} replace />;
  if (loading) return <div className="p-10"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const setStatus = async (oid, status) => {
    await apiClient.patch(`/service-orders/${oid}`, { status });
    load();
  };

  const stats = {
    active: orders.filter(o => !["completed", "cancelled"].includes(o.status)).length,
    completed: orders.filter(o => o.status === "completed").length,
    revenue: orders.filter(o => o.status === "completed").reduce((s, o) => s + (o.amount_sar || 0), 0),
  };

  if (partners.length === 0) {
    return (
      <div className="max-w-xl mx-auto p-12 text-center">
        <Building2 className="w-12 h-12 mx-auto text-[#c9a55c] opacity-60 mb-4" />
        <h2 className="font-display text-2xl mb-2">{lang === "ar" ? "لا توجد ملفات شريك بعد" : "No partner profile yet"}</h2>
        <Link to="/join?intent=partner" className="btn-primary mx-auto inline-flex mt-4"><Plus className="w-4 h-4" /> {lang === "ar" ? "إنشاء ملف شريك" : "Create profile"}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-5 py-10" data-testid="partner-dashboard">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="overline overline-gold mb-1">{lang === "ar" ? "لوحة الشريك" : "Partner portal"}</div>
          <h1 className="font-display text-4xl">{lang === "ar" ? "ملفاتي وطلباتي" : "My profiles & orders"}</h1>
        </div>
        <Link to="/join?intent=partner" className="btn-ghost text-sm"><Plus className="w-4 h-4" /> {lang === "ar" ? "إضافة ملف" : "Add profile"}</Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="surface rounded-xl p-4"><div className="text-xs text-[#6b6b6b] uppercase">{lang === "ar" ? "ملفاتي" : "Profiles"}</div><div className="font-display text-3xl mt-1">{partners.length}</div></div>
        <div className="surface rounded-xl p-4"><div className="text-xs text-[#6b6b6b] uppercase">{lang === "ar" ? "طلبات نشطة" : "Active orders"}</div><div className="font-display text-3xl mt-1 text-[#8a6f2e]">{stats.active}</div></div>
        <div className="surface rounded-xl p-4"><div className="text-xs text-[#6b6b6b] uppercase">{lang === "ar" ? "مكتملة" : "Completed"}</div><div className="font-display text-3xl mt-1 text-green-700">{stats.completed}</div></div>
        <div className="surface rounded-xl p-4"><div className="text-xs text-[#6b6b6b] uppercase">{lang === "ar" ? "إيرادات" : "Revenue"}</div><div className="font-display text-2xl mt-1">{formatSAR(stats.revenue)} <span className="text-xs text-[#8a8a8a]">SAR</span></div></div>
      </div>

      <h2 className="font-display text-2xl mb-4">{lang === "ar" ? "ملفاتي" : "My profiles"}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {partners.map(p => (
          <Link key={p.id} to={`/services/partner/${p.id}`} className="surface rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs uppercase tracking-widest text-[#8a6f2e]">{p.category}</span>
              {p.verified ? <BadgeCheck className="w-4 h-4 text-green-600" /> : p.is_new && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{lang === "ar" ? "جديد" : "New"}</span>}
            </div>
            <div className="font-display text-lg">{p.name_ar}</div>
            <div className="text-xs text-[#6b6b6b]">{p.city}</div>
            <div className="flex justify-between mt-3 text-sm">
              <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 text-[#c9a55c] fill-current" /> {p.rating_avg?.toFixed(1) || "—"} ({p.rating_count || 0})</span>
              <span className="text-[#8a6f2e] font-semibold">{p.orders_count || 0} {lang === "ar" ? "طلب" : "orders"}</span>
            </div>
          </Link>
        ))}
      </div>

      <h2 className="font-display text-2xl mb-4 flex items-center gap-2"><ListChecks className="w-5 h-5 text-[#8a6f2e]" /> {lang === "ar" ? "الطلبات الواردة" : "Incoming orders"} ({orders.length})</h2>
      {orders.length === 0 ? (
        <div className="surface rounded-xl p-10 text-center text-[#6b6b6b]">{lang === "ar" ? "لا طلبات بعد." : "No orders yet."}</div>
      ) : (
        <div className="space-y-3">
          {orders.map(o => {
            const st = STATUS_LABEL[o.status] || STATUS_LABEL.new;
            return (
              <div key={o.id} className="surface rounded-xl p-4" data-testid={`partner-order-${o.id}`}>
                <div className="flex justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-display text-base">{o.code} — {o.service_name || o.partner_category}</div>
                    <div className="text-xs text-[#6b6b6b] mt-1">{o.contact_name} · {o.contact_phone} · {new Date(o.created_at).toLocaleString()}</div>
                    {o.notes && <p className="text-sm text-[#3a3a3a] mt-2">{o.notes}</p>}
                    {o.vehicle_summary && <div className="text-xs text-[#8a6f2e] mt-1">🚗 {o.vehicle_summary}</div>}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full bg-${st.color}-100 text-${st.color}-700 font-semibold self-start`}>{lang === "ar" ? st.ar : st.en}</span>
                </div>
                <div className="flex gap-1 mt-3 flex-wrap">
                  {["in_review", "accepted", "in_progress", "completed", "cancelled"].map(s => (
                    <button key={s} onClick={() => setStatus(o.id, s)} className={`btn-ghost text-xs ${o.status === s ? "border-[#c9a55c]" : ""}`} data-testid={`set-status-${o.id}-${s}`}>
                      {lang === "ar" ? STATUS_LABEL[s].ar : STATUS_LABEL[s].en}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
